import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    Upload,
    Settings,
    Smartphone,
    CheckCircle,
    AlertCircle,
    Loader2,
    FileText,
    ToggleLeft,
    ToggleRight,
    Eye,
    EyeOff,
    RefreshCw,
    Plus,
    Trash2,
    Edit3,
    Save,
    X,
    KeyRound,
    ShieldCheck,
    Server,
    Activity,
    Database
} from 'lucide-react';
import { apiFetch, apiFetchForMultipart } from '../api';
import AnimatedBackground from './Background';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';

// Modal component
const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-slate-800/80 border border-purple-500/30 rounded-2xl shadow-2xl p-8 max-w-md w-full relative transform transition-all duration-300 scale-95 animate-in fade-in-0 zoom-in-95">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                </button>
                {children}
            </div>
        </div>
    );
};

export default function FCMConfig({ onBack, setError, API_BASE_URL, loginId, deliveryMode }) {

    const { client } = useWebSocket();

    const [configs, setConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadAppName, setUploadAppName] = useState('');
    const [managerEmail, setManagerEmail] = useState('');
    const [showJsonData, setShowJsonData] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setErrorLocal] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [uploadError, setUploadError] = useState('');

    // New state for modals and update flow
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [configToUpdate, setConfigToUpdate] = useState(null);
    const [licenseNo, setLicenseNo] = useState('');
    const [licenseError, setLicenseError] = useState('');

    // Queue management state
    const [selectedQueueType, setSelectedQueueType] = useState('0');
    const [currentQueueType, setCurrentQueueType] = useState(null);
    const [isLoadingQueue, setIsLoadingQueue] = useState(false);
    const [isUpdatingQueue, setIsUpdatingQueue] = useState(false);
    const [queueSuccess, setQueueSuccess] = useState('');
    const [queueError, setQueueError] = useState('');

    // update manager email
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [updatedManagerEmail, setUpdatedManagerEmail] = useState('');
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

    // App Name status management state
    const [appNameStatuses, setAppNameStatuses] = useState({});
    const [isLoadingAppStatuses, setIsLoadingAppStatuses] = useState(false);
    const [isUpdatingAppStatus, setIsUpdatingAppStatus] = useState(false);
    const [updatingAppName, setUpdatingAppName] = useState(null);

    // Queue type options
    const queueTypes = [
        { value: '0', label: 'Local Queue', icon: <Server className="w-4 h-4" />, color: 'blue' },
        { value: '1', label: 'Kafka Queue', icon: <Activity className="w-4 h-4" />, color: 'green' },
        { value: '2', label: 'Redis Queue', icon: <Database className="w-4 h-4" />, color: 'red' }
    ];

    useEffect(() => {
        if (selectedConfig) {
            setUpdatedManagerEmail(selectedConfig.notification_email || '');
            setIsEditingEmail(false);
        }
    }, [selectedConfig]);

    // Clear messages after timeout
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess('');
                setErrorLocal('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    useEffect(() => {
        if (uploadSuccess || uploadError) {
            const timer = setTimeout(() => {
                setUploadSuccess('');
                setUploadError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [uploadSuccess, uploadError]);

    useEffect(() => {
        if (queueSuccess || queueError) {
            const timer = setTimeout(() => {
                setQueueSuccess('');
                setQueueError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [queueSuccess, queueError]);

    // Fetch current running queue type
    const fetchCurrentQueueType = async () => {
        setIsLoadingQueue(true);
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setIsLoadingQueue(false);
                    return;
                }

                const topic = `/topic/running/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            setCurrentQueueType(data.data.toString());
                        }
                    } catch (e) {
                        console.error('Failed to parse queue type socket response', e);
                    } finally {
                        subscription.unsubscribe();
                        setIsLoadingQueue(false);
                    }
                });

                // Matches API param: type=0
                client.publish({
                    destination: '/app/running/queue',
                    body: JSON.stringify({ type: '0' })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch('/running/queue?type=0', {
                    method: 'GET'
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.STATUS === 'SUCCESS') {
                        setCurrentQueueType(data.data.toString());
                    }
                }
                setIsLoadingQueue(false);
            }
        } catch (err) {
            console.error("Error fetching current queue type:", err);
            setIsLoadingQueue(false);
        }
    };

    // Fetch all FCM app name statuses
    const fetchAppNameStatuses = async () => {
        setIsLoadingAppStatuses(true);
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setQueueError('Socket connection failed.');
                    setIsLoadingAppStatuses(false);
                    return;
                }

                const topic = `/topic/get/all/fcm/appName/status/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && data.DATA) {
                            setAppNameStatuses(data.DATA);
                        }
                    } catch (e) {
                        console.error('Failed to parse app statuses socket response', e);
                    } finally {
                        subscription.unsubscribe();
                        setIsLoadingAppStatuses(false);
                    }
                });

                client.publish({
                    destination: '/app/get/all/fcm/appName/status',
                    body: JSON.stringify({})
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch('/get/all/fcm/appName/status', {
                    method: 'GET'
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.STATUS === 'SUCCESS' && data.DATA) {
                        setAppNameStatuses(data.DATA);
                    }
                }
                setIsLoadingAppStatuses(false);
            }
        } catch (err) {
            console.error("Error fetching app name statuses:", err);
            setQueueError('Failed to load app name statuses. Network error.');
            setIsLoadingAppStatuses(false);
        }
    };

    // Toggle app name status (active/inactive)
    const toggleAppNameStatus = async (appName, currentStatus) => {
        setIsUpdatingAppStatus(true);
        setUpdatingAppName(appName);
        setQueueSuccess('');
        setQueueError('');

        const newStatus = currentStatus ? 'false' : 'true';

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setQueueError('Socket connection failed.');
                    setIsUpdatingAppStatus(false);
                    setUpdatingAppName(null);
                    return;
                }

                const topic = `/topic/active-or-de-active/fcm/appName/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            const statusText = newStatus === 'true' ? 'activated' : 'deactivated';
                            setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
                            fetchAppNameStatuses();
                        } else {
                            setQueueError(`Failed to update app status. ${data.MESSAGE || 'Unknown error occurred.'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse toggle app status socket response', e);
                        setQueueError('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdatingAppStatus(false);
                        setUpdatingAppName(null);
                    }
                });

                client.publish({
                    destination: '/app/active-or-de-active/fcm/appName/queue',
                    body: JSON.stringify({
                        appName: appName,
                        active: newStatus
                    })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/active-or-de-active/fcm/appName/queue?appName=${appName}&active=${newStatus}`, {
                    method: 'PUT'
                });

                const data = await response.json();

                if (response.ok && data.STATUS === 'SUCCESS') {
                    const statusText = newStatus === 'true' ? 'activated' : 'deactivated';
                    setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
                    await fetchAppNameStatuses();
                } else {
                    setQueueError(`Failed to update app status. ${data.MESSAGE || 'Unknown error occurred.'}`);
                }
                setIsUpdatingAppStatus(false);
                setUpdatingAppName(null);
            }
        } catch (err) {
            console.error("Error updating app name status:", err);
            setQueueError('Failed to update app status. Network error.');
            setIsUpdatingAppStatus(false);
            setUpdatingAppName(null);
        }
    };

    // Update queue type
    const updateQueueType = async () => {
        setIsUpdatingQueue(true);
        setQueueSuccess('');
        setQueueError('');

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setQueueError('Socket connection failed.');
                    setIsUpdatingQueue(false);
                    return;
                }

                const topic = `/topic/active-or-de-active/fcm/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            const queueLabel = queueTypes.find(q => q.value === selectedQueueType)?.label;
                            setQueueSuccess(`Queue type switched to ${queueLabel} successfully!`);
                            setCurrentQueueType(selectedQueueType);
                        } else {
                            setQueueError(`Failed to update queue type. ${data.MESSAGE || 'Unknown error occurred.'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse update queue type socket response', e);
                        setQueueError('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdatingQueue(false);
                    }
                });

                client.publish({
                    destination: '/app/active-or-de-active/fcm/queue',
                    body: JSON.stringify({
                        type: selectedQueueType
                    })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/active-or-de-active/fcm/queue?type=${selectedQueueType}`, {
                    method: 'POST'
                });

                const data = await response.json();

                if (response.ok && data.STATUS === 'SUCCESS') {
                    const queueLabel = queueTypes.find(q => q.value === selectedQueueType)?.label;
                    setQueueSuccess(`Queue type switched to ${queueLabel} successfully!`);
                    setCurrentQueueType(selectedQueueType);
                } else {
                    setQueueError(`Failed to update queue type. ${data.MESSAGE || 'Unknown error occurred.'}`);
                }
                setIsUpdatingQueue(false);
            }
        } catch (err) {
            console.error("Error updating queue type:", err);
            setQueueError('Failed to update queue type. Network error.');
            setIsUpdatingQueue(false);
        }
    };

    // Fetch all configurations
    const fetchConfigs = async () => {
        setIsLoading(true);
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setErrorLocal('Socket connection failed.');
                    setIsLoading(false);
                    return;
                }

                const topic = `/topic/get/json/config/details/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                            const processedConfigs = data.Response.map(config => ({
                                ...config,
                                decodedJson: config.fcm_file_type === 'json' && config.json_data ? JSON.parse(atob(config.json_data)) : null
                            }));
                            setConfigs(processedConfigs);

                            if (processedConfigs.length > 0 && !selectedConfig) {
                                setSelectedConfig(processedConfigs[0]);
                            } else if (selectedConfig) {
                                const updatedSelected = processedConfigs.find(c => c._id === selectedConfig._id);
                                if (updatedSelected) {
                                    setSelectedConfig(updatedSelected);
                                }
                            }
                        } else {
                            setErrorLocal('Failed to load configurations via socket.');
                        }
                    } catch (e) {
                        console.error('Failed to parse configs socket response', e);
                        setErrorLocal('Failed to parse configuration data.');
                    } finally {
                        subscription.unsubscribe();
                        setIsLoading(false);
                    }
                });

                client.publish({
                    destination: '/app/get/json/config/details',
                    body: JSON.stringify({})
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/get/json/config/details`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();

                if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                    const processedConfigs = data.Response.map(config => ({
                        ...config,
                        decodedJson: config.fcm_file_type === 'json' && config.json_data ? JSON.parse(atob(config.json_data)) : null
                    }));
                    setConfigs(processedConfigs);

                    if (processedConfigs.length > 0 && !selectedConfig) {
                        setSelectedConfig(processedConfigs[0]);
                    } else if (selectedConfig) {
                        const updatedSelected = processedConfigs.find(c => c._id === selectedConfig._id);
                        if (updatedSelected) {
                            setSelectedConfig(updatedSelected);
                        }
                    }
                } else {
                    setErrorLocal('Failed to load configurations.');
                }
                setIsLoading(false);
            }
        } catch (err) {
            console.error("Error fetching configs:", err);
            setErrorLocal('Failed to load configurations. Network error.');
            setIsLoading(false);
        }
    };

    // Step 3: User confirms the action (Active/Deactive Config)
    const executeStatusUpdate = async () => {
        if (!configToUpdate || !licenseNo) return;

        setIsConfirmModalOpen(false);
        setIsUpdating(true);
        const newStatusFlag = configToUpdate.status === 'ACTIVE' ? 'false' : 'true';

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setErrorLocal('Socket connection failed.');
                    setIsUpdating(false);
                    return;
                }

                const topic = `/topic/active/deactive/json/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            const newStatus = data.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE';
                            setSuccess(`Configuration ${newStatus.toLowerCase()}d successfully!`);

                            fetchCurrentQueueType();
                            fetchAppNameStatuses();

                            const updatedConfigs = configs.map(c =>
                                c._id === configToUpdate._id ? { ...c, status: newStatus } : c
                            );
                            setConfigs(updatedConfigs);

                            if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                                setSelectedConfig(prev => ({ ...prev, status: newStatus }));
                            }
                        } else {
                            setErrorLocal(`Failed to update: ${data.message || 'Unknown error'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse config status update socket response', e);
                        setErrorLocal('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdating(false);
                        setConfigToUpdate(null);
                        setLicenseNo('');
                    }
                });

                client.publish({
                    destination: '/app/active/deactive/json',
                    body: JSON.stringify({
                        id: configToUpdate._id,
                        active: newStatusFlag,
                        licenseNumber: licenseNo
                    })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(
                    `/active/deactive/json?id=${configToUpdate._id}&active=${newStatusFlag}&licenseNo=${licenseNo}`,
                    {
                        method: 'PUT'
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.STATUS === 'SUCCESS') {
                    const newStatus = data.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE';
                    setSuccess(`Configuration ${newStatus.toLowerCase()}d successfully!`);

                    await fetchCurrentQueueType();
                    await fetchAppNameStatuses();

                    const updatedConfigs = configs.map(c =>
                        c._id === configToUpdate._id ? { ...c, status: newStatus } : c
                    );
                    setConfigs(updatedConfigs);

                    if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                        setSelectedConfig(prev => ({ ...prev, status: newStatus }));
                    }

                } else {
                    setErrorLocal(`Failed to update: ${data.message || 'Unknown error'}`);
                }
                setIsUpdating(false);
                setConfigToUpdate(null);
                setLicenseNo('');
            }
        } catch (err) {
            console.error("Error toggling config status:", err);
            setErrorLocal('Failed to update configuration status. Network error.');
            setIsUpdating(false);
            setConfigToUpdate(null);
            setLicenseNo('');
        }
    };

    // Step 1: User clicks the toggle button
    const handleToggleClick = (config) => {
        setConfigToUpdate(config);
        setIsLicenseModalOpen(true);
    };

    // Step 2: User submits the license number
    const handleLicenseSubmit = () => {
        if (!licenseNo.trim()) {
            setLicenseError('License number cannot be empty.');
            return;
        }
        setLicenseError('');
        setIsLicenseModalOpen(false);
        setIsConfirmModalOpen(true);
    };

    // Handle file upload
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && (file.name.endsWith('.json') || file.name.endsWith('.p12'))) {
            setSelectedFile(file);
            setUploadError('');
        } else {
            setSelectedFile(null);
            setUploadError('Please select a valid .json or .p12 file.');
        }
    };

    // Upload JSON or P12 configuration
    const handleUploadJson = async () => {
        setIsUploading(true);
        setUploadSuccess('');
        setUploadError('');

        if (!selectedFile) {
            setUploadError('Please select a JSON or P12 file to upload.');
            setIsUploading(false);
            return;
        }

        if (!uploadAppName.trim()) {
            setUploadError('Please provide an App Name for the file upload.');
            setIsUploading(false);
            return;
        }

        if (!managerEmail.trim()) {
            setUploadError('Please provide an Manager Email for the file upload.');
            setIsUploading(false);
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await apiFetchForMultipart(`/upload-json?appName=${uploadAppName}&notification_email=${managerEmail}`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok && data.status === 'SUCCESS') {
                setUploadSuccess(`Configuration uploaded successfully! ✅`);
                setSelectedFile(null);
                setUploadAppName('');
                setManagerEmail('');
                const fileInput = document.getElementById('json-file-upload');
                if (fileInput) fileInput.value = '';
                fetchConfigs();
                fetchCurrentQueueType();
                fetchAppNameStatuses();
            } else {
                setUploadError(`Failed to upload file. ${data.MESSAGE || 'Unknown error occurred.'}`);
            }
        } catch (err) {
            console.error("File Upload error:", err);
            setUploadError('Failed to upload file. Network error.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleUpdateManagerEmail = async (config) => {
        if (!updatedManagerEmail.trim()) {
            setErrorLocal('Manager email cannot be empty.');
            return;
        }

        setIsUpdatingEmail(true);
        setErrorLocal('');
        setSuccess('');

        try {
            if (deliveryMode === 'socket') {
                await ensureSocketConnection(client);

                const topic = `/topic/update/fcm/manager-email/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;

                        if (data.status === 'SUCCESS') {
                            setSuccess('Manager email updated successfully!');
                            fetchConfigs();
                            setIsEditingEmail(false);
                        } else {
                            setErrorLocal(data.MESSAGE || 'Failed to update email.');
                        }
                    } catch {
                        setErrorLocal('Invalid server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdatingEmail(false);
                    }
                });

                client.publish({
                    destination: '/app/update/fcm/manager-email',
                    body: JSON.stringify({
                        id: config._id,
                        notification_email: updatedManagerEmail
                    })
                });

            } else {
                const response = await apiFetch(
                    `/update/fcm/manager-email?appName=${config._id}&notification_email=${updatedManagerEmail}`,
                    { method: 'PUT' }
                );

                const data = await response.json();

                if (response.ok && data.status === 'SUCCESS') {
                    setSuccess('Manager email updated successfully!');
                    fetchConfigs();
                    setIsEditingEmail(false);
                } else {
                    setErrorLocal(data.MESSAGE || 'Failed to update email.');
                }

                setIsUpdatingEmail(false);
            }
        } catch (err) {
            console.error(err);
            setErrorLocal('Network error while updating email.');
            setIsUpdatingEmail(false);
        }
    };

    const handleRefresh = async () => {
        fetchConfigs();
        fetchCurrentQueueType();
        fetchAppNameStatuses();
    }

    // Initial load
    useEffect(() => {
        fetchConfigs();
        fetchCurrentQueueType();
        fetchAppNameStatuses();
    }, [API_BASE_URL]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return timestamp;
        }
    };

    const getCurrentQueueLabel = () => {
        return queueTypes.find(q => q.value === currentQueueType)?.label || 'Unknown';
    };

    const getCurrentQueueColor = () => {
        return queueTypes.find(q => q.value === currentQueueType)?.color || 'gray';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] font-inter text-white">
            {/* Render the animated background */}
            <AnimatedBackground />

            <div className="relative z-10 p-4 md:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl transition-all duration-300"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Back to Dashboard</span>
                        </button>
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                                <Settings className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold">FCM Configuration</h1>
                                <p className="text-white/60">Manage Firebase Cloud Messaging settings & Queue</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={fetchCurrentQueueType}
                            disabled={isLoadingQueue}
                            className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 rounded-xl text-orange-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoadingQueue ? 'animate-spin' : ''}`} />
                            <span>Queue Status</span>
                        </button>
                        <button
                            onClick={fetchAppNameStatuses}
                            disabled={isLoadingAppStatuses}
                            className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 rounded-xl text-cyan-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoadingAppStatuses ? 'animate-spin' : ''}`} />
                            <span>App Status</span>
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 rounded-xl text-purple-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Global Messages */}
                {(success || error || queueSuccess || queueError) && (
                    <div className="mb-6 space-y-3">
                        {(success || error) && (
                            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${success
                                ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                : 'bg-red-500/20 border-red-500/40 text-red-200'
                                }`}>
                                {success ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-medium">{success || error}</span>
                            </div>
                        )}
                        {(queueSuccess || queueError) && (
                            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${queueSuccess
                                ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                : 'bg-red-500/20 border-red-500/40 text-red-200'
                                }`}>
                                {queueSuccess ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-medium">{queueSuccess || queueError}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Sidebar - App List */}
                    <div className="lg:col-span-1">
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <Smartphone className="w-5 h-5 text-blue-400" />
                                <h3 className="text-lg font-semibold">Applications</h3>
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-200 text-xs rounded-full">
                                    {configs.length}
                                </span>
                            </div>

                            {isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                                    <span className="ml-3 text-white/80">Loading...</span>
                                </div>
                            ) : configs.length > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                                    {configs.map((config) => {
                                        const appNameStatus = appNameStatuses[config._id];
                                        const isAppActive = appNameStatus === true || appNameStatus === 'true';
                                        const isCurrentlyUpdating = updatingAppName === config._id && isUpdatingAppStatus;

                                        return (
                                            <div
                                                key={config._id}
                                                onClick={() => setSelectedConfig(config)}
                                                className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] ${selectedConfig?._id === config._id
                                                    ? 'bg-blue-500/20 border-blue-400/50'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="font-medium text-sm">{config._id}</h4>
                                                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${config.status === 'ACTIVE'
                                                        ? 'bg-green-500/20 text-green-300'
                                                        : 'bg-red-500/20 text-red-300'
                                                        }`}>
                                                        <div className={`w-2 h-2 rounded-full ${config.status === 'ACTIVE' ? 'bg-green-400' : 'bg-red-400'
                                                            }`}></div>
                                                        <span>{config.status}</span>
                                                    </div>
                                                </div>

                                                {/* App Name Status Toggle */}
                                                <div className="flex items-center justify-between mb-2 bg-white/5 rounded-lg p-2">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-xs text-white/60">App Status:</span>
                                                        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${isAppActive
                                                            ? 'bg-green-500/20 text-green-300'
                                                            : appNameStatus === false || appNameStatus === 'false'
                                                                ? 'bg-red-500/20 text-red-300'
                                                                : 'bg-gray-500/20 text-gray-300'
                                                            }`}>
                                                            <div className={`w-2 h-2 rounded-full ${isAppActive ? 'bg-green-400' :
                                                                appNameStatus === false || appNameStatus === 'false' ? 'bg-red-400' : 'bg-gray-400'
                                                                }`}></div>
                                                            <span>
                                                                {isLoadingAppStatuses ? 'Loading...' :
                                                                    isAppActive ? 'Active' :
                                                                        appNameStatus === false || appNameStatus === 'false' ? 'Inactive' : 'Unknown'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent selecting the config
                                                            toggleAppNameStatus(config._id, isAppActive);
                                                        }}
                                                        disabled={isCurrentlyUpdating || isLoadingAppStatuses || appNameStatus === undefined}
                                                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-300 text-xs ${isAppActive
                                                            ? 'bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30'
                                                            : 'bg-green-500/20 border border-green-500/40 text-green-200 hover:bg-green-500/30'
                                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {isCurrentlyUpdating ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : isAppActive ? (
                                                            <ToggleRight className="w-4 h-4" />
                                                        ) : (
                                                            <ToggleLeft className="w-4 h-4" />
                                                        )}
                                                        <span className="font-medium">
                                                            {isCurrentlyUpdating ? 'Updating...' : isAppActive ? 'Deactivate' : 'Activate'}
                                                        </span>
                                                    </button>
                                                </div>

                                                <p className="text-xs text-white/50">
                                                    Created: {formatTimestamp(config.creation_time_str)}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-white/60">
                                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No configurations found</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Content Area */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Queue Management Section */}
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-cyan-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-white">Queue Management</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Current Queue Status */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${currentQueueType !== null ? `bg-${getCurrentQueueColor()}-500/20` : 'bg-gray-500/20'
                                            }`}>
                                            {isLoadingQueue ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                queueTypes.find(q => q.value === currentQueueType)?.icon || <Server className="w-4 h-4" />
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-white/60 text-xs uppercase tracking-wider">Current Queue</label>
                                            <p className={`font-medium text-sm ${currentQueueType !== null ? `text-${getCurrentQueueColor()}-300` : 'text-gray-300'
                                                }`}>
                                                {isLoadingQueue ? 'Loading...' : getCurrentQueueLabel()}
                                            </p>
                                        </div>
                                    </div>
                                    {currentQueueType !== null && (
                                        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs bg-${getCurrentQueueColor()}-500/20 text-${getCurrentQueueColor()}-300`}>
                                            <div className={`w-2 h-2 rounded-full bg-${getCurrentQueueColor()}-400 animate-pulse`}></div>
                                            <span>Active</span>
                                        </div>
                                    )}
                                </div>

                                {/* Queue Type Selection */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Switch Queue Type</label>
                                    <select
                                        value={selectedQueueType}
                                        onChange={(e) => setSelectedQueueType(e.target.value)}
                                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300 mb-4"
                                        disabled={isUpdatingQueue}
                                    >
                                        {queueTypes.map((type) => (
                                            <option key={type.value} value={type.value} className="bg-slate-800 text-white">
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={updateQueueType}
                                        disabled={isUpdatingQueue || selectedQueueType === currentQueueType}
                                        className="w-full py-2 rounded-lg font-medium text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                                    >
                                        {isUpdatingQueue ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Updating...</span>
                                            </div>
                                        ) : (
                                            <span>Switch Queue Type</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Upload form */}
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                                    <Upload className="w-5 h-5 text-orange-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-white">Upload FCM Configuration</h3>
                            </div>

                            {(uploadSuccess || uploadError) && (
                                <div className={`mb-6 p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${uploadSuccess
                                    ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                    : 'bg-red-500/20 border-red-500/40 text-red-200'
                                    }`}>
                                    {uploadSuccess ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                    <span className="font-medium">{uploadSuccess || uploadError}</span>
                                </div>
                            )}

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-3">
                                        App Name
                                    </label>
                                    <input
                                        type="text"
                                        value={uploadAppName}
                                        onChange={(e) => setUploadAppName(e.target.value)}
                                        placeholder="Enter App Name for configuration..."
                                        className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300"
                                        disabled={isUploading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-3">
                                        Manager Notification Email
                                    </label>
                                    <input
                                        type="text"
                                        value={managerEmail}
                                        onChange={(e) => setManagerEmail(e.target.value)}
                                        placeholder="Enter Manager Email for configuration..."
                                        className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300"
                                        disabled={isUploading}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="json-file-upload" className="block text-white/90 text-sm font-medium mb-3">
                                        Select Configuration File (.json or .p12)
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="json-file-upload"
                                            type="file"
                                            accept=".json,.p12"
                                            onChange={handleFileChange}
                                            className="block w-full text-sm text-white/80 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-orange-500/20 file:text-orange-200 hover:file:bg-orange-500/30 file:transition-all file:duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer bg-white/5 border border-white/20 rounded-xl p-2"
                                            disabled={isUploading}
                                        />
                                    </div>
                                    {selectedFile && (
                                        <div className="mt-3 p-3 bg-orange-500/20 border border-orange-500/40 rounded-xl">
                                            <p className="text-sm text-orange-200 flex items-center">
                                                <FileText className="w-4 h-4 mr-2" />
                                                Selected: <span className="font-medium ml-1">{selectedFile.name}</span>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={handleUploadJson}
                                    disabled={isUploading || !selectedFile || !uploadAppName.trim()}
                                    className="w-full py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-600 hover:via-red-600 hover:to-pink-600 disabled:opacity-50 group"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                    {isUploading ? (
                                        <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Uploading...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                                            <Upload className="w-5 h-5" />
                                            <span>Upload Configuration</span>
                                        </div>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Configuration Details */}
                        {selectedConfig && (
                            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                                            <Settings className="w-5 h-5 text-blue-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold">{selectedConfig._id}</h3>
                                            <p className="text-white/60 text-sm">Details</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        <span className="text-white/80 text-sm">Status:</span>
                                        <button
                                            onClick={() => handleToggleClick(selectedConfig)}
                                            disabled={isUpdating}
                                            className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${selectedConfig.status === 'ACTIVE'
                                                ? 'bg-green-500/20 border border-green-500/40 text-green-200 hover:bg-green-500/30'
                                                : 'bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30'
                                                } disabled:opacity-50`}
                                        >
                                            {isUpdating && configToUpdate?._id === selectedConfig._id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : selectedConfig.status === 'ACTIVE' ? (
                                                <ToggleRight className="w-5 h-5" />
                                            ) : (
                                                <ToggleLeft className="w-5 h-5" />
                                            )}
                                            <span className="font-medium">
                                                {selectedConfig.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div className="space-y-4">
                                        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                            <label className="text-white/60 text-xs uppercase tracking-wider">App ID</label>
                                            <p className="font-medium mt-1">{selectedConfig._id}</p>
                                        </div>

                                        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                            <label className="text-white/60 text-xs uppercase tracking-wider">
                                                Manager Email
                                            </label>

                                            {!isEditingEmail ? (
                                                <div className="flex items-center justify-between mt-1">
                                                    <p className="font-medium">{selectedConfig.notification_email}</p>
                                                    <button
                                                        onClick={() => setIsEditingEmail(true)}
                                                        className="text-cyan-300 hover:text-cyan-200 transition"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="space-y-3 mt-2">
                                                    <input
                                                        type="email"
                                                        value={updatedManagerEmail}
                                                        onChange={(e) => setUpdatedManagerEmail(e.target.value)}
                                                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                                                        placeholder="Enter manager email"
                                                    />

                                                    <div className="flex justify-end space-x-3">
                                                        <button
                                                            onClick={() => {
                                                                setUpdatedManagerEmail(selectedConfig.notification_email);
                                                                setIsEditingEmail(false);
                                                            }}
                                                            className="px-4 py-2 bg-white/10 rounded-lg"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>

                                                        <button
                                                            onClick={() => handleUpdateManagerEmail(selectedConfig)}
                                                            disabled={isUpdatingEmail}
                                                            className="px-4 py-2 bg-green-600 rounded-lg"
                                                        >
                                                            {isUpdatingEmail ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Save className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                            <label className="text-white/60 text-xs uppercase tracking-wider">Creation Date</label>
                                            <p className="font-medium mt-1">{selectedConfig.date}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                            <label className="text-white/60 text-xs uppercase tracking-wider">Status</label>
                                            <div className="flex items-center space-x-2 mt-1">
                                                <div className={`w-3 h-3 rounded-full ${selectedConfig.status === 'ACTIVE' ? 'bg-green-400' : 'bg-red-400'
                                                    }`}></div>
                                                <span className={`font-medium ${selectedConfig.status === 'ACTIVE' ? 'text-green-300' : 'text-red-300'
                                                    }`}>
                                                    {selectedConfig.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                            <label className="text-white/60 text-xs uppercase tracking-wider">Last Updated</label>
                                            <p className="font-medium mt-1">{formatTimestamp(selectedConfig.creation_time_str)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="text-white/90 text-sm font-medium">Firebase Configuration</label>
                                        <button
                                            onClick={() => setShowJsonData(!showJsonData)}
                                            className="flex items-center space-x-2 px-3 py-1 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white/80 transition-all duration-300"
                                            disabled={selectedConfig.fcm_file_type === 'p12'}
                                        >
                                            {showJsonData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            <span className="text-sm">{showJsonData ? 'Hide' : 'Show'} Content</span>
                                        </button>
                                    </div>

                                    {showJsonData && selectedConfig.fcm_file_type === 'p12' ? (
                                        <div className="bg-slate-900/50 border border-white/10 rounded-xl p-4 text-white/80 text-sm">
                                            This is a keystore file (.p12) and is not viewable.
                                        </div>
                                    ) : showJsonData && selectedConfig.decodedJson ? (
                                        <div className="bg-slate-900/50 border border-white/10 rounded-xl p-4 overflow-auto max-h-96 custom-scrollbar">
                                            <pre className="text-green-300 text-sm whitespace-pre-wrap font-mono">
                                                {JSON.stringify(selectedConfig.decodedJson, null, 2)}
                                            </pre>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* License Modal */}
            <Modal isOpen={isLicenseModalOpen} onClose={() => setIsLicenseModalOpen(false)}>
                <div className="text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-500/20 border border-purple-500/40 mb-6">
                        <KeyRound className="h-8 w-8 text-purple-300" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">License Key Required</h3>
                    <p className="text-white/70 mb-6">Please enter your license number to proceed with this action.</p>
                    <input
                        type="text"
                        value={licenseNo}
                        onChange={(e) => {
                            setLicenseNo(e.target.value);
                            if (licenseError) setLicenseError('');
                        }}
                        placeholder="Enter license number..."
                        className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
                    />
                    {licenseError && <p className="text-red-400 text-sm mt-2">{licenseError}</p>}
                    <div className="mt-8 flex justify-end space-x-4">
                        <button onClick={() => setIsLicenseModalOpen(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 transition-colors">Cancel</button>
                        <button onClick={handleLicenseSubmit} className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 font-semibold transition-colors">Submit</button>
                    </div>
                </div>
            </Modal>

            {/* Confirmation Modal */}
            <Modal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
                {configToUpdate && (
                    <div className="text-center">
                        <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full border mb-6 ${configToUpdate.status === 'ACTIVE' ? 'bg-red-500/20 border-red-500/40' : 'bg-green-500/20 border-green-500/40'
                            }`}>
                            <ShieldCheck className={`h-8 w-8 ${configToUpdate.status === 'ACTIVE' ? 'text-red-300' : 'text-green-300'
                                }`} />
                        </div>
                        <h3 className="text-2xl font-bold mb-3">Confirm Action</h3>
                        <p className="text-white/70 mb-6">
                            Are you sure you want to <span className="font-bold">{configToUpdate.status === 'ACTIVE' ? 'deactivate' : 'activate'}</span> the configuration for <span className="font-bold text-purple-300">{configToUpdate._id}</span>?
                        </p>
                        <div className="mt-8 flex justify-end space-x-4">
                            <button onClick={() => setIsConfirmModalOpen(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 transition-colors">Cancel</button>
                            <button
                                onClick={executeStatusUpdate}
                                className={`px-6 py-3 rounded-xl font-semibold transition-colors ${configToUpdate.status === 'ACTIVE' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                {isUpdating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Yes, ${configToUpdate.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}`}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <style jsx>{`
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(100, 116, 139, 0.5) rgba(51, 65, 85, 0.5);
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background-color: rgba(51, 65, 85, 0.5);
                    border-radius: 0.5rem;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(100, 116, 139, 0.5);
                    border-radius: 0.5rem;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(100, 116, 139, 0.7);
                }
            `}</style>
        </div>
    );
}